import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useShallow } from "zustand/react/shallow";
import type { ChatViewModel } from "../../lib/store.js";
import { useRealtimeStore } from "../../lib/store.js";

const EMPTY_IDS: string[] = [];
const PAGE_SIZE = 10;

interface ForwardTargetPickerProps {
  // Источник пересылки (живёт в store как forwardComposition).
  sourceChatId: string;
  sourceMessageIds: string[];
  hideSenderName: boolean;
  hideCaption: boolean;
  onClose(): void;
}

// Модалка «куда переслать»: открывается кнопкой из forward-превью у
// композера. Как в оригинальном Telegram, выбор чата НЕ отправляет
// пересылку сразу: пикер открывает выбранный чат, forwardComposition
// остаётся плашкой над композером (ПКМ — «без отправителя / без текста»),
// а отправка происходит по Send. Раньше форвард улетал по клику без
// возможности что-то настроить — жалоба заказчика (раунд 46).
//
// Селекторы написаны через стабильные подписки (массив id-строк +
// state.chats по reference). Объекты внутри селектора НЕ создаём —
// это раньше провоцировало React #185 на больших аккаунтах, см.
// spa-state-pitfalls п.8.
export const ForwardTargetPicker = ({
  sourceChatId,
  sourceMessageIds,
  hideSenderName,
  hideCaption,
  onClose
}: ForwardTargetPickerProps) => {
  const selectChat = useRealtimeStore((state) => state.selectChat);
  const selectedChatId = useRealtimeStore((state) => state.selectedChatId);
  const activeListId = useRealtimeStore((state) => state.activeDialogListId);
  const orderedListIds = useRealtimeStore(
    useShallow((state) => state.chatIdsByList[state.activeDialogListId] ?? EMPTY_IDS)
  );
  const chatsById = useRealtimeStore((state) => state.chats);
  const backendHasMore = useRealtimeStore(
    (state) => state.dialogHasMoreById[state.activeDialogListId] ?? true
  );
  const backendIsLoading = useRealtimeStore(
    (state) => state.dialogListStateById[state.activeDialogListId]?.isLoading ?? false
  );
  const loadMoreDialogs = useRealtimeStore((state) => state.loadMoreDialogs);
  const searchDialogs = useRealtimeStore((state) => state.searchDialogs);
  const clearDialogSearch = useRealtimeStore((state) => state.clearDialogSearch);
  const dialogSearchChatIds = useRealtimeStore(
    useShallow((state) => state.dialogSearch.chatIds)
  );
  const dialogSearchLoading = useRealtimeStore(
    (state) => state.dialogSearch.loading
  );
  const dialogSearchQuery = useRealtimeStore(
    (state) => state.dialogSearch.query
  );

  // Исходный чат из списка не исключаем: «переслать этому же юзеру»
  // — легальный сценарий (несколько сообщений ему же).
  const allCandidates = useMemo(() => {
    const result: ChatViewModel[] = [];
    for (const chatId of orderedListIds) {
      const chat = chatsById[chatId];
      if (!chat) {
        continue;
      }
      result.push(chat);
    }
    return result;
  }, [orderedListIds, chatsById]);

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [query, setQuery] = useState("");
  const trimmedQuery = query.trim();
  const trimmedLower = trimmedQuery.toLowerCase();
  const localFiltered = useMemo(() => {
    if (trimmedLower.length === 0) {
      return allCandidates;
    }
    return allCandidates.filter((chat) =>
      chat.title.toLowerCase().includes(trimmedLower)
    );
  }, [allCandidates, trimmedLower]);

  // Серверный поиск (chat.searchDialogs) умеет находить по Telegram ID,
  // @username, +телефону — как в основном поиске клиента. Дёргаем со
  // задержкой 250мс от последнего ввода, чтобы не бомбить воркер.
  useEffect(() => {
    if (trimmedQuery.length === 0) {
      clearDialogSearch();
      return;
    }
    const timer = window.setTimeout(() => {
      searchDialogs(trimmedQuery);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [trimmedQuery, searchDialogs, clearDialogSearch]);

  useEffect(() => () => clearDialogSearch(), [clearDialogSearch]);

  const serverResults = useMemo(() => {
    if (trimmedQuery.length === 0 || dialogSearchQuery !== trimmedQuery) {
      return [] as ChatViewModel[];
    }
    const seen = new Set(localFiltered.map((chat) => chat.chatId));
    const result: ChatViewModel[] = [];
    for (const chatId of dialogSearchChatIds) {
      if (seen.has(chatId)) {
        continue;
      }
      const chat = chatsById[chatId];
      if (chat) {
        result.push(chat);
        seen.add(chatId);
      }
    }
    return result;
  }, [trimmedQuery, dialogSearchQuery, dialogSearchChatIds, chatsById, localFiltered]);

  const filtered = useMemo(
    () => (serverResults.length > 0 ? [...localFiltered, ...serverResults] : localFiltered),
    [localFiltered, serverResults]
  );
  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visible.length;

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [trimmedLower]);

  // Авто-цикл: пока worker говорит hasMore=true, дёргаем loadMoreDialogs.
  // На worker'е prefetch уже наполняет кэш, ответы должны приходить быстро.
  // Без этого пикер открывался с initial-50 чатами, и юзер видел частичный
  // список (для больших аккаунтов критично).
  // Backoff по застою: если список не вырос после ответа — 600мс → 1.2с → …
  // → 30с. Раньше запрос уходил сразу после каждого ответа, пока воркер
  // отвечал hasMore=true (для больших папок — всегда): открытый пикер
  // качал полный снапшот папки раз в секунду часами (прод 2026-10-15).
  const pickerBackoffRef = useRef<{ listId: string; maxCount: number; stalls: number }>({
    listId: "",
    maxCount: -1,
    stalls: 0
  });
  const listCount = orderedListIds.length;
  useEffect(() => {
    if (!backendHasMore || backendIsLoading) {
      return;
    }
    const backoff = pickerBackoffRef.current;
    if (backoff.listId !== activeListId || listCount > backoff.maxCount) {
      backoff.listId = activeListId;
      backoff.maxCount = listCount;
      backoff.stalls = 0;
    } else {
      backoff.stalls += 1;
    }
    const delay = Math.min(600 * 2 ** backoff.stalls, 30_000);
    const timer = window.setTimeout(() => {
      loadMoreDialogs(activeListId);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [backendHasMore, backendIsLoading, activeListId, listCount, loadMoreDialogs]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = (targetChatId: string): void => {
    if (targetChatId !== selectedChatId) {
      selectChat(targetChatId);
    }
    onClose();
  };

  return createPortal(
    <div
      className="forward-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Куда переслать"
    >
      <button
        type="button"
        className="forward-modal__backdrop"
        onClick={onClose}
        aria-label="Закрыть"
      />
      <div className="forward-modal__panel">
        <div className="forward-sheet__header">
          <div>
            <strong>Куда переслать?</strong>
            <p>
              {sourceMessageIds.length > 1
                ? `${sourceMessageIds.length} сообщ. · `
                : ""}
              {hideSenderName || hideCaption
                ? `${hideSenderName ? "без отправителя" : ""}${
                    hideSenderName && hideCaption ? ", " : ""
                  }${hideCaption ? "без текста" : ""}`
                : "с указанием отправителя"}
              {" · откроется чат, отправка — кнопкой Send"}
            </p>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Закрыть"
          >
            <i className="ic ic-close" aria-hidden="true" />
          </button>
        </div>
        <div className="forward-sheet__search">
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Поиск: название / @username / Telegram ID / +телефон"
            autoFocus
          />
        </div>
        <div className="forward-sheet__list">
          {visible.length === 0 ? (
            <div className="forward-modal__empty">
              {allCandidates.length === 0
                ? "Нет чатов для пересылки."
                : dialogSearchLoading
                  ? "Ищем в Telegram…"
                  : "Ничего не найдено."}
            </div>
          ) : (
            visible.map((chat) => (
              <button
                key={chat.chatId}
                type="button"
                className="forward-sheet__item"
                onClick={() => submit(chat.chatId)}
              >
                <strong
                  className="forward-sheet__item-title"
                  title={chat.isSavedMessages ? "Избранное" : chat.title}
                >
                  {chat.isSavedMessages ? "Избранное" : chat.title}
                  {chat.chatId === sourceChatId ? " · этот же чат" : ""}
                </strong>
                <span className="forward-sheet__item-preview">
                  {chat.lastMessageText || "Без сообщений"}
                </span>
              </button>
            ))
          )}
          {hasMore ? (
            <button
              type="button"
              className="forward-sheet__more"
              onClick={() => setVisibleCount((value) => value + PAGE_SIZE)}
            >
              Показать ещё ({filtered.length - visible.length})
            </button>
          ) : backendHasMore ? (
            <button
              type="button"
              className="forward-sheet__more"
              onClick={() => {
                loadMoreDialogs(activeListId);
                setVisibleCount((value) => value + PAGE_SIZE);
              }}
              disabled={backendIsLoading}
            >
              {backendIsLoading
                ? "Загружаем из Telegram…"
                : "Загрузить ещё чаты из Telegram"}
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
};
